import { z } from 'zod';
import { uuidSchema } from './common.js';

/**
 * customer_org_jurisdiction DTOs (SIL-642, db/schema/758_customer_org_jurisdiction.sql).
 *
 * One record per customer_org: country code, registration id, and
 * denied-party screening status/timestamp. Prerequisite data for export
 * control (plans/exchange-mechanics-and-export-control.md 2b). `screening`
 * fields are defined now but only ever written by SIL-649 (deferred) —
 * this DTO layer does not build screening integration.
 */

export const isoCountryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'expected ISO 3166-1 alpha-2 (e.g. US, GB)')
  .brand('IsoCountryCode');
export type IsoCountryCode = z.infer<typeof isoCountryCodeSchema>;

export const screeningStatusSchema = z.enum(['unscreened', 'clear', 'flagged', 'blocked']);
export type ScreeningStatus = z.infer<typeof screeningStatusSchema>;

export const customerOrgJurisdictionSchema = z.object({
  customerOrgId: uuidSchema,
  /** NULL = unclassified — the backfilled state for existing orgs, treated as restricted downstream. */
  countryCode: isoCountryCodeSchema.nullable(),
  registrationId: z.string().nullable(),
  screeningStatus: screeningStatusSchema,
  screenedAt: z.string().nullable(),
});
export type CustomerOrgJurisdiction = z.infer<typeof customerOrgJurisdictionSchema>;

export const customerOrgJurisdictionResponseSchema = z.object({
  data: customerOrgJurisdictionSchema,
});
export type CustomerOrgJurisdictionResponse = z.infer<typeof customerOrgJurisdictionResponseSchema>;

/** Ops correction payload — country + registration id only; screening status is SIL-649's write path, not this one. */
export const customerOrgJurisdictionUpdateSchema = z.object({
  countryCode: isoCountryCodeSchema.nullable().optional(),
  registrationId: z.string().trim().min(1).max(64).nullable().optional(),
});
export type CustomerOrgJurisdictionUpdate = z.infer<typeof customerOrgJurisdictionUpdateSchema>;

/** Buyer/seller onboarding-complete payload — country is required to finish onboarding. */
export const onboardingJurisdictionSchema = z.object({
  countryCode: isoCountryCodeSchema,
});
export type OnboardingJurisdiction = z.infer<typeof onboardingJurisdictionSchema>;
